import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { cx } from '@/styles/muiTheme';
import ListSeasons from './ListSeasons';

type Props = {
  leagueId?: number;
  seasonId: string;
  onSeasonChange: (seasonId: string) => void;
  currentMonth: Date;
  onMonthChange: (date: Date) => void;
  gameDates?: string[];
};

const monthKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}`;

const navButtonSx = {
  color: cx.text.primary,
  '&:hover': { bgcolor: cx.bg.surface, borderColor: cx.border.strong },
  '&.Mui-disabled': { opacity: 0.35, color: cx.text.primary, borderColor: cx.border.strong },
};

// Lives above GamesList rather than inside the calendar: GamesList remounts
// behind a full loading overlay on every month/season change, which would hide
// the very controls that triggered it.
const GamesCalendarToolbar = ({
  leagueId,
  seasonId,
  onSeasonChange,
  currentMonth,
  onMonthChange,
  gameDates,
}: Props) => {
  const [monthMenuAnchor, setMonthMenuAnchor] = useState<HTMLElement | null>(null);

  // Months of the season that have games, with a count each. The dates are
  // distinct days for a single team, so a day is a game.
  const months = useMemo(() => {
    const byMonth = new Map<string, { month: Date; count: number }>();
    for (const dateStr of gameDates ?? []) {
      const d = parseISO(dateStr);
      const entry = byMonth.get(monthKey(d));
      if (entry) entry.count += 1;
      else byMonth.set(monthKey(d), { month: new Date(d.getFullYear(), d.getMonth(), 1), count: 1 });
    }
    return Array.from(byMonth.values()).sort((a, b) => a.month.getTime() - b.month.getTime());
  }, [gameDates]);

  const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 15);
  const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 15);

  // Bounded to the season's first and last month with games. Unbounded until
  // the dates load, so navigation isn't frozen while they're in flight.
  const first = months[0]?.month;
  const last = months[months.length - 1]?.month;
  const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const prevDisabled = !!first && monthStart <= first;
  const nextDisabled = !!last && monthStart >= last;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
      <Box sx={{ width: 248 }}>
        {leagueId && (
          <ListSeasons key={leagueId} leagueId={leagueId} value={seasonId} onChange={onSeasonChange} />
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<ChevronLeftIcon />}
          onClick={() => onMonthChange(prevMonth)}
          disabled={prevDisabled}
          sx={navButtonSx}
        >
          {format(prevMonth, 'MMM yyyy')}
        </Button>
        <Button
          variant="outlined"
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(e) => setMonthMenuAnchor(e.currentTarget)}
          sx={{ ...navButtonSx, bgcolor: cx.bg.surface, borderColor: cx.border.default }}
        >
          {format(currentMonth, 'MMM yyyy')}
        </Button>
        <Menu
          anchorEl={monthMenuAnchor}
          open={Boolean(monthMenuAnchor)}
          onClose={() => setMonthMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { width: 180, mt: 0.75 } } }}
        >
          {months.map(({ month, count }) => {
            const selected = monthKey(month) === monthKey(currentMonth);
            return (
              <MenuItem
                key={month.getTime()}
                selected={selected}
                onClick={() => {
                  onMonthChange(month);
                  setMonthMenuAnchor(null);
                }}
                sx={{
                  justifyContent: 'space-between',
                  gap: 2,
                  fontSize: 12,
                  lineHeight: '16px',
                  color: selected ? cx.text.primary : cx.text.tertiary,
                }}
              >
                {format(month, 'MMMM yyyy')}
                <Box component="span" sx={{ fontSize: 10, color: cx.text.muted }}>
                  {count} games
                </Box>
              </MenuItem>
            );
          })}
        </Menu>
        <Button
          variant="outlined"
          endIcon={<ChevronRightIcon />}
          onClick={() => onMonthChange(nextMonth)}
          disabled={nextDisabled}
          sx={navButtonSx}
        >
          {format(nextMonth, 'MMM yyyy')}
        </Button>
      </Box>
    </Box>
  );
};

export default GamesCalendarToolbar;
